// Candidate / contact / application search results → rich cards or a table.

import type {
  BlockOutput,
  CandidateBlockData,
  CandidateSummary,
  DataTableBlockData,
  SearchResultScope,
} from '../../types';
import { num, posNum, str } from './guards';
import { cell, colIndex, mapSuggestedActions } from './shared';
import type { StructuredRowMeta, StructuredTable } from './types';

// Interview evidence sometimes arrives as a raw JSON blob (not prose) — drop it.
const isStructuredBlob = (snippet: string): boolean =>
  /\{\s*"[^"]+"\s*:/.test(snippet);

// In block-HTML snippets the tags carry structure, so collapse redundant
// newlines (else RichHtml's \n→<br/> doubles the gaps); plain text keeps them.
const BLOCK_HTML = /<(p|div|br|ul|ol|li|table|h[1-6])\b/i;
const cleanSnippet = (snippet: string): string =>
  (BLOCK_HTML.test(snippet) ? snippet.replace(/\n+/g, ' ') : snippet).trim();

// One row → CandidateSummary via best-effort column lookup. profileHref comes
// straight from the backend's `hrefs[i]` — the UI never builds deep links.
const rowToCandidate = (
  row: string[],
  id: string,
  columns: string[] | undefined,
  profileHref: string | undefined,
  meta?: StructuredRowMeta
): CandidateSummary => {
  const score = posNum(meta?.match_score);
  const badge = str(meta?.match_badge);
  return {
    id,
    // Contact and application searches ship the same columns
    // (["Name", "Role", "Email"]), so one lookup covers both.
    name: cell(row, colIndex(columns, 'Name')) || `#${id}`,
    headline:
      cell(row, colIndex(columns, 'Role')) ||
      cell(row, colIndex(columns, 'Title')) ||
      cell(row, colIndex(columns, 'Position')),
    tags: [],
    profileHref,
    email: cell(row, colIndex(columns, 'Email')) || undefined,
    matchReason: str(meta?.match_reason) || undefined,
    matchBadge: badge || undefined,
    matchScore: score ? { value: score, label: badge } : undefined,
    yearsExperience: posNum(meta?.years_experience),
    hasActiveApplication: meta?.has_active_application === true,
    applicationStatuses: meta?.application_statuses ?? [],
    jobCount: num(meta?.job_count) ?? 0,
    badges: Array.isArray(meta?.badges)
      ? meta.badges
          .filter((b) => b && typeof b.label === 'string' && b.label !== '')
          .map((b) => ({
            key: str(b.key) ?? '',
            label: b.label as string,
            tone: str(b.tone),
          }))
      : undefined,
    milestones: Array.isArray(meta?.milestones) ? meta.milestones : undefined,
    matchedAspects: Array.isArray(meta?.matched_aspects)
      ? meta.matched_aspects
      : undefined,
    evidence: Array.isArray(meta?.evidence)
      ? meta.evidence
          .filter(
            (e) =>
              e &&
              typeof e.snippet === 'string' &&
              e.snippet !== '' &&
              !isStructuredBlob(e.snippet)
          )
          .map((e) => ({
            aspect: str(e.aspect) ?? '',
            snippet: cleanSnippet(e.snippet as string),
            rating: num(e.rating),
          }))
      : undefined,
  };
};

// nexus applied_filters → "Label: value" chips (e.g. "Company: JioStar").
// A pipe-joined skill is an ALTERNATIVE group ("Python|Java" = either
// satisfies the gate) — display it as prose, never the internal syntax.
const appliedFilterChips = (
  f: StructuredTable['applied_filters']
): string[] | undefined => {
  if (!f) return undefined;
  const chips: string[] = [];
  for (const c of f.required_companies ?? []) chips.push(`Company: ${c}`);
  for (const s of f.required_skills ?? [])
    chips.push(`Skill: ${s.split('|').join(' or ')}`);
  for (const e of f.required_education ?? []) chips.push(`Education: ${e}`);
  return chips.length > 0 ? chips : undefined;
};

const hasAppliedFilters = (
  filters: StructuredTable['applied_filters']
): boolean =>
  !!filters &&
  Object.values(filters).some((value) =>
    Array.isArray(value)
      ? value.length > 0
      : typeof value === 'string'
        ? value.trim().length > 0
        : value !== null && value !== undefined && value !== false
  );

const searchEntity = (
  entity: string | undefined
): SearchResultScope['entity'] =>
  entity === 'application'
    ? 'application'
    : entity === 'candidate'
      ? 'candidate'
      : 'contact';

const buildSearchScope = (
  t: StructuredTable
): SearchResultScope | undefined => {
  const explicitlyFiltered = t.result_scope === 'filtered_search';
  const nonNegativeCount = (value: unknown): number | undefined =>
    typeof value === 'number' && Number.isFinite(value) && value >= 0
      ? value
      : undefined;
  const matchingCount =
    (explicitlyFiltered
      ? nonNegativeCount(t.filtered_match_count)
      : posNum(t.filtered_match_count)) ??
    (t.answer?.type === 'count' ? nonNegativeCount(t.answer.value) : undefined);
  const exactMatchingCount =
    posNum(t.exact_filter_match_count) ?? posNum(t.matched_count);
  const totalMatchingCount =
    matchingCount === undefined
      ? exactMatchingCount
      : exactMatchingCount === undefined
        ? matchingCount
        : Math.max(matchingCount, exactMatchingCount);
  const filters = appliedFilterChips(t.applied_filters);
  if (
    matchingCount === undefined &&
    !exactMatchingCount &&
    !explicitlyFiltered &&
    !hasAppliedFilters(t.applied_filters)
  ) {
    return undefined;
  }
  return {
    entity: searchEntity(t.entity),
    matchingCount: totalMatchingCount,
    exactMatchingCount,
    query: str(t.query)?.trim() || undefined,
    appliedFilters: filters,
    workspaceTotalKnown:
      typeof t.workspace_total_known === 'boolean'
        ? t.workspace_total_known
        : false,
  };
};

export const buildCandidateBlock = (
  t: StructuredTable,
  toolCallId?: string
): BlockOutput => {
  const rows = t.rows ?? [];
  const ids = t.ids ?? [];
  const hrefs = t.hrefs ?? [];
  const meta = t.row_meta ?? [];
  // Payload order is final — agentforge already sorted by the reranker's
  // llm_score, so rows/ids/app_ids/row_meta arrive highest-match-first.
  const candidates = rows.map((r, i) =>
    rowToCandidate(r, ids[i] ?? String(i), t.columns, hrefs[i], meta[i])
  );
  // Retain nexus's legacy exact-filter count for compatibility. The visible
  // result-count explanation uses buildSearchScope and never treats it as
  // workspace inventory.
  const matchedCount = posNum(t.matched_count);
  // strong_count is ignored unless it leaves at least one row visible AND hides
  // at least one — a 0 folded EVERY row behind the "weaker matches" toggle.
  const rawStrong = t.strong_count;
  const strongCount =
    typeof rawStrong === 'number' && rawStrong >= 1 && rawStrong < rows.length
      ? rawStrong
      : undefined;
  const data: CandidateBlockData = {
    summary:
      rows.length === 1 ? `1 candidate.` : `Top ${rows.length} candidates.`,
    candidates,
    suggestedActions: mapSuggestedActions(t.suggested_actions),
    // Chips substitute {first_appid} for kula_application_get calls.
    appIds: t.app_ids,
    strongCount,
    matchedCount,
    answer: str(t.answer?.explanation) || undefined,
    matchWarning: t.match_warning,
    appliedFilterChips: appliedFilterChips(t.applied_filters),
    searchScope: buildSearchScope(t),
    loadMore:
      t.has_more === true && str(t.cache_key)
        ? {
            cacheKey: t.cache_key as string,
            offset: num(t.next_offset) ?? rows.length,
            remaining:
              typeof t.total === 'number' && t.total > rows.length
                ? t.total - rows.length
                : undefined,
          }
        : undefined,
    toolCallId,
  };
  return { type: 'candidate', data };
};

// Render candidate search rows as a data-table when the user asked for one.
export const buildCandidateTableBlock = (
  t: StructuredTable,
  toolCallId?: string
): BlockOutput => {
  const rows = t.rows ?? [];
  const ids = t.ids ?? [];
  const meta = t.row_meta ?? [];
  const baseColumns = t.columns ?? ['Name', 'Role', 'Email'];
  // Append a Match column when rows carry relevance signal.
  const hasMatch = meta.some(
    (m) => m?.match_badge || typeof m?.match_score === 'number'
  );
  const columns = hasMatch ? [...baseColumns, 'Match'] : baseColumns;
  const tableRows = rows.map((r, i) => {
    if (!hasMatch) return r;
    const m = meta[i];
    const score = posNum(m?.match_score);
    const pct = score ? ` ${Math.round(score * 100)}%` : '';
    return [...r, `${m?.match_badge ?? ''}${pct}`.trim()];
  });
  const data: DataTableBlockData = {
    intro: '',
    variant: 'list',
    columns,
    rows: tableRows,
    ids: ids.length > 0 ? ids : undefined,
    searchScope: buildSearchScope(t),
    suggestedActions: mapSuggestedActions(t.suggested_actions),
    toolCallId,
  };
  return { type: 'dataTable', data };
};
